import { useReducer, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { initialState, loginFormReducer } from "./loginFormReducer";
import { validate } from "@/utils/validators";
import { loginSchema } from "./auth.schema";
import { login } from "@/api/authService";
import { parseApiError } from "@/utils/errorHandler";
import { getPageForRole } from "@/routes/routeMap";
import type { parsedApiError } from "@/types/api/errors.api";
import type { loginFormData } from "./types.api";

export const useLoginForm = () => {
    const [state, dispatch] = useReducer(loginFormReducer, initialState);
    const navigate = useNavigate();
    const auth = useAuth();

    const handleInputChange = useCallback(
        (field: keyof loginFormData, value: string) => {
            dispatch({ type: "SET_FIELD", field, value });
        },
        [],
    );

    const togglePasswordVisibility = useCallback(() => {
        dispatch({ type: "TOGGLE_PASSWORD_VISIBILITY" });
    }, []);

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            dispatch({ type: "RESET_ERROR" });

            const validationErrors = validate(state.formData, loginSchema);
            if (Object.keys(validationErrors).length > 0) {
                const errorPayload: parsedApiError = {
                    generalMessage: "Por favor, corrija los errores del formulario.",
                    fieldErrors: validationErrors,
                    code: "client_validation_error",
                };
                dispatch({ type: "SET_FORM_ERRORS", payload: errorPayload });
                return;
            }

            dispatch({ type: "SUBMIT_START" });
            try {
                const loginResponse = await login(state.formData);
                auth.login(loginResponse.user, loginResponse.access);
                dispatch({ type: "SUBMIT_SUCCESS" });
                navigate(getPageForRole(loginResponse.user.role));
            } catch (error) {
                const formattedError = parseApiError(error);
                dispatch({ type: "SUBMIT_FAILURE", payload: formattedError });
            }
        },
        [state.formData, navigate, auth],
    );

    return {
        state,
        handleInputChange,
        togglePasswordVisibility,
        handleSubmit,
    };
};
